import gql from 'graphql-tag';
import { SUPPLIER_ORDERING_TIME_FOR_DAY_FRAGMENT } from './supplier';

const BAD_EXPIRY_DATE_ISSUE_FRAGMENT = gql`
    fragment BadExpiryDateIssueFragment on BadExpiryDateIssue {
        expiryDate
        minSupplierExpiryDays
        productId
        quantity
        type
    }
`;

const BROKEN_QUANTITY_ISSUE_FRAGMENT = gql`
    fragment BrokenQuantityIssueFragment on BrokenQuantityIssue {
        brokenQuantity
        productId
        type
    }
`;

const DIFFERENT_PACKAGING_ISSUE_FRAGMENT = gql`
    fragment DifferentPackagingIssueFragment on DifferentPackagingIssue {
        expectedPackaging
        productId
        receivedPackaging
        type
    }
`;

const DIFFERENT_QUANTITY_ISSUE_FRAGMENT = gql`
    fragment DifferentQuantityIssueFragment on DifferentQuantityIssue {
        expectedQuantity
        productId
        receivedQuantity
        type
    }
`;

const NEW_BARCODE_ISSUE_FRAGMENT = gql`
    fragment NewBarcodeIssueFragment on NewBarcodeIssue {
        barcode
        productId
        type
    }
`;

const NOT_RECEIVED_ISSUE_FRAGMENT = gql`
    fragment NotReceivedIssueFragment on NotReceivedIssue {
        productId
        type
    }
`;

const UNKNOWN_PRODUCT_ISSUE_FRAGMENT = gql`
    fragment UnknownProductIssueFragment on UnknownProductIssue {
        barcode
        description
        type
    }
`;

const WRONG_PRODUCT_ISSUE_FRAGMENT = gql`
    fragment WrongProductIssueFragment on WrongProductIssue {
        barcode
        productId
        type
    }
`;

export const SUPPLY_ORDER_PRODUCT_ISSUE_FRAGMENT = gql`
    fragment SupplyOrderProductIssueFragment on SupplyOrderProductIssue {
        ... on BadExpiryDateIssue {
            ...BadExpiryDateIssueFragment
        }
        ... on BrokenQuantityIssue {
            ...BrokenQuantityIssueFragment
        }
        ... on DifferentPackagingIssue {
            ...DifferentPackagingIssueFragment
        }
        ... on DifferentQuantityIssue {
            ...DifferentQuantityIssueFragment
        }
        ... on NewBarcodeIssue {
            ...NewBarcodeIssueFragment
        }
        ... on NotReceivedIssue {
            ...NotReceivedIssueFragment
        }
        ... on UnknownProductIssue {
            ...UnknownProductIssueFragment
        }
        ... on WrongProductIssue {
            ...WrongProductIssueFragment
        }
    }
    ${BAD_EXPIRY_DATE_ISSUE_FRAGMENT}
    ${BROKEN_QUANTITY_ISSUE_FRAGMENT}
    ${DIFFERENT_PACKAGING_ISSUE_FRAGMENT}
    ${DIFFERENT_QUANTITY_ISSUE_FRAGMENT}
    ${NEW_BARCODE_ISSUE_FRAGMENT}
    ${NOT_RECEIVED_ISSUE_FRAGMENT}
    ${UNKNOWN_PRODUCT_ISSUE_FRAGMENT}
    ${WRONG_PRODUCT_ISSUE_FRAGMENT}
`;

export const SUPPLY_ORDER_FRAGMENT = gql`
    fragment SupplyOrderFragment on SupplyOrder {
        _id
        state
        createdAt
        orderDate
        nextOrderDate
        orderingTermsValidity {
            isValid
            deliveryPrice
        }
        dateDeliveryScheduled
        dateDelivery
        scheduledDeliveryDateValidity
        number
        priceHT
        priceTTC
        supplier {
            _id
            name
            odooSupplierId
            orderMode
            shouldOrderAutomatically
            orderingTimes {
                monday {
                    ...OrderingTimeForDayFragment
                }
                tuesday {
                    ...OrderingTimeForDayFragment
                }
                wednesday {
                    ...OrderingTimeForDayFragment
                }
                thursday {
                    ...OrderingTimeForDayFragment
                }
                friday {
                    ...OrderingTimeForDayFragment
                }
                saturday {
                    ...OrderingTimeForDayFragment
                }
                sunday {
                    ...OrderingTimeForDayFragment
                }
            }
            orderingProductTerms {
                minQuantity
                productId
            }
            orderingTerms {
                deliveryPrice
                minRequirements {
                    unit
                    value
                }
            }
        }
        warehouseColumn {
            _id
            name
        }
        weeklyMenusStartDate
    }
    ${SUPPLIER_ORDERING_TIME_FOR_DAY_FRAGMENT}
`;

/**
 * L'achat tel que la fiche de détail l'affiche par défaut : l'entête, ses lignes commandées, et de quoi
 * juger les conditions de commande. Aucun champ « catalogue » ni « prévisions » ici — ils vivent dans
 * leurs propres requêtes, chargées à l'ouverture des sections correspondantes.
 */
export const SUPPLY_ORDER_DETAILS_FRAGMENT = gql`
    fragment SupplyOrderDetailsFragment on SupplyOrder {
        _id
        comment
        createdAt
        dateBilling
        dateDelivery
        dateDeliveryScheduled
        description
        hourDelivery
        invoiceNumber
        isMerged
        issues {
            ...SupplyOrderProductIssueFragment
        }
        nextOrderDate
        number
        odooPurchaseOrderName
        odooPurchaseOrderUrl
        odooReceiptPickingUrl
        orderDate
        orderingTermsValidity {
            isValid
            deliveryPrice
        }
        priceHT
        priceHTInvoice
        priceTTCInvoice
        products {
            _id
            availableStock
            brand
            colis
            conditionning
            confirmedQuantity
            confirmedQuantityHistory {
                createdAt
                quantity
            }
            fullname
            isToBeArchived
            minSupplierOrderQuantity
            name
            packagesPerBatch
            price
            productReceivedDetails {
                brokenItems
                colis
                conditionning
                quantity
                expiryDate
            }
            quantity
            quantityGenerated
            quantityGeneratedForMenus
            stock
            storedAt
            supplierShortageInfo {
                inShortage
                returnDate
            }
            # Clé de normalisation du cache Apollo pour SupplyOrderProduct (['supplyOrderId', '_id']) :
            # sans elle, l'écriture du résultat échoue et la requête ne rend jamais de données.
            supplyOrderId
            tva
            unitPrice
            volume
        }
        scheduledDeliveryDateHistory {
            scheduledDeliveryDate
            createdAt
        }
        scheduledDeliveryDateValidity
        soldOutRiskProductIds
        spreadsheetId
        spreadsheetName
        state
        stateHistory {
            createdAt
            state
            createdBy
            createdByName
        }
        supplier {
            _id
            clientCode
            odooSupplierId
            frequency
            name
            orderMode
            orderEmails
            orderingTimes {
                monday {
                    ...OrderingTimeForDayFragment
                }
                tuesday {
                    ...OrderingTimeForDayFragment
                }
                wednesday {
                    ...OrderingTimeForDayFragment
                }
                thursday {
                    ...OrderingTimeForDayFragment
                }
                friday {
                    ...OrderingTimeForDayFragment
                }
                saturday {
                    ...OrderingTimeForDayFragment
                }
                sunday {
                    ...OrderingTimeForDayFragment
                }
            }
            orderingProductTerms {
                minQuantity
                productId
            }
            orderingTerms {
                deliveryPrice
                minRequirements {
                    unit
                    value
                }
            }
        }
        temperature
        totalQuantity
        totalQuantityReceived
        totalVolume
        totalWeight
        updatedAt
        warehouseColumn {
            _id
            name
        }
        weeklyMenusStartDate
    }
    ${SUPPLY_ORDER_PRODUCT_ISSUE_FRAGMENT}
    ${SUPPLIER_ORDERING_TIME_FOR_DAY_FRAGMENT}
`;

/** Le catalogue du fournisseur restant à ajouter à l'achat. Lourd : chargé à la demande. */
export const SUPPLY_ORDER_PRODUCT_TO_ADD_FRAGMENT = gql`
    fragment SupplyOrderProductToAddFragment on SupplierProductInfoWithStock {
        _id
        availableStock
        brand
        conditionning
        fullname
        isToBeArchived
        name
        packagesPerBatch
        stock
        supplierShortageInfo {
            inShortage
            returnDate
        }
        tva
        unitPrice
        volume
    }
`;

/**
 * Fragment « lourd », conservé pour les ACHATS MANUELS seulement : cet écran travaille sur le catalogue
 * entier du fournisseur (`productsInfoWithStock`), qui charge le stock et le stock disponible de chaque
 * référence. La fiche de détail d'un achat, elle, lit `SupplyOrderDetailsFragment`.
 */
export const SUPPLY_ORDER_WITH_SUPPLIER_PRODUCTS_INFO_FRAGMENT = gql`
    fragment SupplyOrderWithSupplierProductsInfoFragment on SupplyOrder {
        _id
        comment
        createdAt
        dateDelivery
        dateDeliveryScheduled
        description
        hourDelivery
        issue
        issues {
            ...SupplyOrderProductIssueFragment
        }
        justInTime
        number
        odooPurchaseOrderName
        odooPurchaseOrderUrl
        orderDate
        nextOrderDate
        orderingTermsValidity {
            isValid
            deliveryPrice
        }
        scheduledDeliveryDateHistory {
            scheduledDeliveryDate
            createdAt
        }
        scheduledDeliveryDateValidity
        priceHT
        priceHTInvoice
        priceTTCInvoice
        products {
            _id
            brand
            colis
            confirmedQuantity
            confirmedQuantityHistory {
                createdAt
                quantity
            }
            conditionning
            packagesPerBatch
            isToBeArchived
            name
            fullname
            price
            productReceivedDetails {
                brokenItems
                colis
                conditionning
                quantity
                expiryDate
            }
            quantity
            quantityGenerated
            quantityGeneratedExtended
            quantityGeneratedForMenus
            supplierShortageInfo {
                inShortage
                returnDate
            }
            stock
            storedAt
            supplyOrderId
            availableStock
            tva
            unitPrice
            volume
            minSupplierOrderQuantity
        }
        soldOutRiskProductIds
        spreadsheetId
        spreadsheetName
        state
        stateHistory {
            createdAt
            state
            createdBy
            createdByName
        }
        productsInfoWithStock {
            _id
            availableStock
            brand
            conditionning
            fullname
            imageUrl
            isToBeArchived
            name
            packagesPerBatch
            stock
            supplierShortageInfo {
                inShortage
                returnDate
            }
            tva
            unitPrice
            volume
        }
        supplier {
            _id
            clientCode
            odooSupplierId
            frequency
            nextOrderingDate
            name
            orderMode
            orderEmails
            orderingTimes {
                monday {
                    ...OrderingTimeForDayFragment
                }
                tuesday {
                    ...OrderingTimeForDayFragment
                }
                wednesday {
                    ...OrderingTimeForDayFragment
                }
                thursday {
                    ...OrderingTimeForDayFragment
                }
                friday {
                    ...OrderingTimeForDayFragment
                }
                saturday {
                    ...OrderingTimeForDayFragment
                }
                sunday {
                    ...OrderingTimeForDayFragment
                }
            }
            orderingProductTerms {
                minQuantity
                productId
            }
            orderingTerms {
                deliveryPrice
                minRequirements {
                    unit
                    value
                }
            }
        }
        temperature
        totalQuantity
        totalQuantityReceived
        totalVolume
        totalWeight
        updatedAt
        warehouseColumn {
            _id
            name
        }
        weeklyMenusStartDate
    }
    ${SUPPLY_ORDER_PRODUCT_ISSUE_FRAGMENT}
    ${SUPPLIER_ORDERING_TIME_FOR_DAY_FRAGMENT}
`;

export const SUPPLY_ORDER_PRODUCT_STOCK_VARIATION_FRAGMENT = gql`
    fragment SupplyOrderProductStockVariationFragment on ProductStockVariation {
        computedStockThreshold
        createdAt
        forecasts {
            forecastsDaysToIgnoreForOrders
            forecastsFreefoodPunctual
            forecastsFreefoodRecurring
            forecastsStoreDry
            lastUpdateDateFreefoodPunctual
            lastUpdateDateFreefoodRecurring
            lastUpdateDateStoreDry
        }
        initialStock
        microstoreDetails {
            missingStockByDay
            targetStockByDay
            targetStockDetails {
                locationTarget
                planogramDate
                siteName
                usedLocationTarget
            }
            totalStock
        }
        missingStockBetweenDeliveries
        orderDate
        operations {
            punctualOrdersNotPrepared
            recurrentOrdersNotPrepared
            validatedStockTransferPrepared
            validatedStockTransferNotPrepared
        }
        outboundFreefoodFloatStockVariation
        outboundStoreFloatStockVariation
        outboundStockVariation
        productId
        productSwitchDetails {
            variations
        }
        stockUpdateDayIndexIfOrderedOnNextOrderingDay
        stockUpdateDayIndexIfOrderedToday
        stockVariation
        stockVariationFromSupplyOrders
        supplyOrderId
    }
`;
